import { useState } from 'react';
import { PACKS, type Theme } from '../game/themes';
import { LANGUAGE_LABELS, type T } from '../i18n/strings';
import { ANDROID_URL, Button, IOS_URL, Logo, Modal, SITE, Toggle } from '../components/ui';
import type { ChatStatus } from '../stream/twitch';
import { packOpen, type ProState, type SiteConfig } from '../pay/pro';
import { PLAYER_COLORS } from '../theme';

export interface Settings {
  names: [string, string];
  packs: string[];
  adult: boolean;
  aiJudge: boolean;
  readAloud: boolean;
  sounds: boolean;
  showLink: boolean;
  channel: string;
  chatTheme: boolean;
  chatJudge: boolean;
}

export const DEFAULT_SETTINGS: Settings = {
  names: ['', ''],
  packs: PACKS.filter((p) => !p.adult).map((p) => p.key),
  adult: false,
  aiJudge: true,
  readAloud: true,
  sounds: true,
  showLink: true,
  channel: '',
  chatTheme: true,
  chatJudge: true,
};

interface Props {
  t: T;
  settings: Settings;
  set: (s: Partial<Settings>) => void;
  themes: Theme[] | null;
  visibleThemes: (pack: string) => number;
  languages: string[];
  lang: string;
  setLang: (l: string) => void;
  chatStatus: ChatStatus;
  onConnect: () => void;
  onDisconnect: () => void;
  onStart: () => void;
  error: string | null;
  config: SiteConfig;
  pro: ProState;
  onPro: () => void;
  hiddenPacks: Set<string>;
}

export function Setup(p: Props) {
  const { t, settings: s, set } = p;
  const [adultAsk, setAdultAsk] = useState(false);
  const [howOpen, setHowOpen] = useState(false);
  const packs = PACKS.filter((x) => !p.hiddenPacks.has(x.key) && (!x.adult || s.adult));
  const steps = t
    .a('game_twenty_challenge_steps')
    .split('||')
    .map((st) => {
      const i = st.indexOf(':');
      return i > 0 ? [st.slice(0, i), st.slice(i + 1).trim()] : ['', st];
    });

  const togglePack = (key: string) =>
    set({ packs: s.packs.includes(key) ? s.packs.filter((k) => k !== key) : [...s.packs, key] });

  return (
    <div className="setup">
      <header className="topbar">
        <a className="brand" href={SITE}>
          <Logo />
          <span>Social Gamebox</span>
        </a>
        <div className="topbar-right">
          <label className="lang">
            <span className="sr-only">{t.t('language')}</span>
            <select value={p.lang} onChange={(e) => p.setLang(e.target.value)}>
              {p.languages.map((l) => (
                <option key={l} value={l}>
                  {LANGUAGE_LABELS[l] ?? l}
                </option>
              ))}
            </select>
          </label>
          <a className="btn btn-secondary small" href={IOS_URL} target="_blank" rel="noopener">
            {t.t('getApp')}
          </a>
        </div>
      </header>

      <section className="hero">
        <div className="hero-badge">$20</div>
        <div>
          <h1>{t.a('game_twenty_challenge')}</h1>
          <p className="tagline">{t.t('tagline')}</p>
          <p className="muted">{t.t('playFree')}</p>
          <button className="link" onClick={() => setHowOpen((v) => !v)} aria-expanded={howOpen}>
            {t.t('howItWorks')} {howOpen ? '▴' : '▾'}
          </button>
          {howOpen && (
            <ol className="steps">
              {steps.map(([h, b], i) => (
                <li key={i}>
                  {h && <b>{h}: </b>}
                  {b}
                </li>
              ))}
            </ol>
          )}
        </div>
      </section>

      <div className="setup-grid">
        <section className="card">
          <h2>{t.t('players')}</h2>
          <div className="names">
            {[0, 1].map((i) => (
              <label key={i} className="name-field" style={{ ['--pc' as string]: PLAYER_COLORS[i] }}>
                <span className="dot" />
                <input
                  value={s.names[i]}
                  maxLength={24}
                  placeholder={t.t('player', { n: i + 1 })}
                  onChange={(e) => {
                    const names = [...s.names] as [string, string];
                    names[i] = e.target.value;
                    set({ names });
                  }}
                />
              </label>
            ))}
          </div>

          <div className="packs-head">
            <h2>{t.t('packs')}</h2>
            <div className="packs-actions">
              <button
                className="link"
                onClick={() => set({ packs: packs.filter((x) => packOpen(x.key, p.config, p.pro)).map((x) => x.key) })}
              >
                {t.t('all')}
              </button>
              <button className="link" onClick={() => set({ packs: [] })}>
                {t.t('none')}
              </button>
            </div>
          </div>
          <div className="packs">
            {packs.map((x) => {
              const open = packOpen(x.key, p.config, p.pro);
              const on = open && s.packs.includes(x.key);
              const count = p.visibleThemes(x.key);
              return (
                <button
                  key={x.key}
                  className={`pack ${on ? 'on' : ''} ${open ? '' : 'locked'} ${x.adult ? 'adult' : ''}`}
                  onClick={() => (open ? togglePack(x.key) : p.onPro())}
                  aria-pressed={on}
                >
                  <span className="pack-check" aria-hidden="true">
                    {open ? (on ? '✓' : '') : '🔒'}
                  </span>
                  <span className="pack-name">{t.a(x.name)}</span>
                  <span className="pack-desc">{t.a(x.desc)}</span>
                  <span className="pack-count">{open ? t.t('themes', { count }) : t.t('pro')}</span>
                </button>
              );
            })}
          </div>
          {!s.adult ? (
            <button className="link adult-link" onClick={() => setAdultAsk(true)}>
              {t.t('show18')}
            </button>
          ) : (
            <button
              className="link adult-link"
              onClick={() => set({ adult: false, packs: s.packs.filter((k) => !PACKS.find((x) => x.key === k)?.adult) })}
            >
              ✓ {t.t('show18')}
            </button>
          )}
        </section>

        <section className="card">
          <h2>{t.a('settings')}</h2>
          <Toggle
            checked={s.aiJudge}
            onChange={(v) => set({ aiJudge: v })}
            label={t.a('twentyChallengeAiJudge')}
            desc={t.a('twentyChallengeAiJudgeDesc')}
          />
          <Toggle checked={s.readAloud} onChange={(v) => set({ readAloud: v })} label={t.t('readAloud')} />
          <Toggle checked={s.sounds} onChange={(v) => set({ sounds: v })} label={t.t('sounds')} />
          <Toggle checked={s.showLink} onChange={(v) => set({ showLink: v })} label={t.t('showLink')} />

          <h2 className="twitch-head">
            <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true">
              <path
                fill="#9146FF"
                d="M11.64 5.93h1.43v4.28h-1.43m3.93-4.28H17v4.28h-1.43M7 2 3.43 5.57v12.86h4.28V22l3.58-3.57h2.85L20.57 12V2m-1.43 9.29-2.85 2.85h-2.86l-2.5 2.5v-2.5H7.71V3.43h11.43Z"
              />
            </svg>
            {t.t('twitch')}
          </h2>
          <p className="muted small">{t.t('twitchDesc')}</p>
          <form
            className="channel"
            onSubmit={(e) => {
              e.preventDefault();
              if (p.chatStatus === 'connected' || p.chatStatus === 'connecting') p.onDisconnect();
              else p.onConnect();
            }}
          >
            <input
              value={s.channel}
              placeholder={t.t('channel')}
              onChange={(e) => set({ channel: e.target.value })}
              disabled={p.chatStatus === 'connected'}
              autoCapitalize="off"
              autoCorrect="off"
              spellCheck={false}
            />
            <Button type="submit" kind="secondary" disabled={!s.channel.trim()}>
              {p.chatStatus === 'connected' || p.chatStatus === 'connecting' ? t.t('disconnect') : t.t('connect')}
            </Button>
          </form>
          <p className={`chat-status ${p.chatStatus}`}>
            {p.chatStatus === 'connecting' && t.t('connecting')}
            {p.chatStatus === 'connected' && `● ${t.t('connected', { channel: s.channel.replace(/^#/, '') })}`}
            {p.chatStatus === 'error' && t.t('connectFailed')}
          </p>
          <Toggle
            checked={s.chatTheme}
            onChange={(v) => set({ chatTheme: v })}
            label={t.t('chatPicksTheme')}
            disabled={p.chatStatus !== 'connected'}
          />
          <Toggle
            checked={s.chatJudge}
            onChange={(v) => set({ chatJudge: v })}
            label={t.t('chatJudges')}
            disabled={p.chatStatus !== 'connected'}
          />

          {p.config.paywall && (
            <div className="pro-box">
              {p.pro.pro ? (
                <span>✓ {t.t('proActive')}</span>
              ) : (
                <Button kind="secondary" onClick={p.onPro}>
                  {t.t('proTitle')}
                </Button>
              )}
            </div>
          )}
        </section>
      </div>

      <div className="start-row">
        {p.error && <p className="error">{p.error}</p>}
        <Button onClick={p.onStart} className="huge" disabled={!p.themes}>
          {p.themes ? t.t('start') : t.t('loading')}
        </Button>
        <p className="muted small">{t.t('streamTip')}</p>
      </div>

      <footer className="footer">
        <div className="stores">
          <a href={IOS_URL} target="_blank" rel="noopener">
            App Store
          </a>
          <a href={ANDROID_URL} target="_blank" rel="noopener">
            Google Play
          </a>
        </div>
        <div className="legal">
          <a href={`${SITE}/impressum`}>{t.t('imprint')}</a>
          <a href={`${SITE}${p.lang === 'de' ? '/de' : ''}/privacy-policy`}>{t.t('privacy')}</a>
          <a href={`${SITE}${p.lang === 'de' ? '/de' : ''}/terms-and-conditions`}>{t.t('terms')}</a>
          <span>© Madami UG (haftungsbeschränkt)</span>
        </div>
      </footer>

      {adultAsk && (
        <Modal onClose={() => setAdultAsk(false)}>
          <h2>{t.t('show18')}</h2>
          <p>{t.t('adultWarning')}</p>
          <div className="modal-actions">
            <Button kind="secondary" onClick={() => setAdultAsk(false)}>
              {t.t('cancel')}
            </Button>
            <Button
              onClick={() => {
                set({ adult: true });
                setAdultAsk(false);
              }}
            >
              {t.t('adultConfirm')}
            </Button>
          </div>
        </Modal>
      )}
    </div>
  );
}
